import React, { useEffect, useRef, useState } from 'react';
import { Bold, Italic, List, ListOrdered, Heading, Link2, Undo2, Redo2 } from 'lucide-react';
import { sanearHtmlDescripcion, htmlDesdePegado } from '../utils/htmlDescripcion';
import { crearHistorial, registrar, deshacer, rehacer, valorActual, puedeDeshacer, puedeRehacer, siguienteBloque } from '../utils/historialEdicion';
import './EditorDescripcion.css';

// Botón de la barra a nivel de módulo (NO dentro del componente: uno inline se remontaría en cada
// render, perdiendo foco/estado). mousedown, no click: no roba el foco/selección del campo.
function BotonBarra({ onDo, title, disabled = false, children }) {
  return (
    <button type="button" className="ed-btn" title={title} aria-label={title} disabled={disabled}
      onMouseDown={(e) => { e.preventDefault(); onDo(); }}>
      {children}
    </button>
  );
}

// IA-1 (editor de descripción enriquecido MÍNIMO, sin dependencia): contenteditable propio + barra con
// los 8 controles. El modelo que sale por onChange al SALIR del campo o al PEGAR está saneado a la lista
// blanca (p, strong, em, ul, ol, li, h3, a); mientras se tipea onChange emite el HTML crudo (no salta el
// cursor; el saneo normaliza al blur). Deshacer/Rehacer usan un historial PROPIO (no el execCommand
// nativo, que no revertía los enlaces). «Subtítulo» alterna la línea entre h3 y párrafo.
export default function EditorDescripcion({ value = '', onChange, id, disabled = false, ariaLabel = 'Descripción larga' }) {
  const ref = useRef(null);
  const rangoRef = useRef(null);           // selección guardada para aplicar el enlace tras tipear la URL
  const histRef = useRef(crearHistorial(value || ''));
  const timerRef = useRef(null);           // debounce del snapshot de tipeo
  const [linkAbierto, setLinkAbierto] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [nav, setNav] = useState({ undo: false, redo: false });

  const sincronizarNav = () => setNav({ undo: puedeDeshacer(histRef.current), redo: puedeRehacer(histRef.current) });
  const capturar = (html) => { histRef.current = registrar(histRef.current, html); sincronizarNav(); };
  const capturarYa = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } if (ref.current) capturar(ref.current.innerHTML); };

  // Sincroniza el valor EXTERNO (carga, sugerencia de IA, saneo al blur) al DOM solo cuando difiere, así
  // tipear no reescribe el nodo (lo que movería el cursor). Un cambio externo entra al historial.
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || '')) {
      ref.current.innerHTML = value || '';
      capturar(value || '');
    }
    // capturar/ref son estables; depender solo del valor externo es intencional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emitir = (sanear) => {
    if (!ref.current || !onChange) return;
    const html = ref.current.innerHTML;
    onChange(sanear ? sanearHtmlDescripcion(html) : html);
  };

  const foco = () => { if (ref.current) ref.current.focus(); };
  const caretAlFinal = () => {
    try {
      const sel = window.getSelection(); const r = document.createRange();
      r.selectNodeContents(ref.current); r.collapse(false); sel.removeAllRanges(); sel.addRange(r);
    } catch (_) { /* jsdom */ }
  };

  // Comando de formato: captura el estado previo (por si había tipeo sin snapshot), ejecuta, captura el
  // nuevo. Así Deshacer revierte exactamente este comando — enlaces incluidos.
  const cmd = (comando, arg) => { capturarYa(); foco(); try { document.execCommand(comando, false, arg); } catch (_) { /* jsdom */ } capturar(ref.current ? ref.current.innerHTML : ''); emitir(false); };

  const toggleSubtitulo = () => {
    let actual = '';
    try { actual = document.queryCommandValue('formatBlock'); } catch (_) { actual = ''; }
    cmd('formatBlock', siguienteBloque(actual));
  };

  const aplicarHistorial = (nuevo) => {
    histRef.current = nuevo;
    const html = valorActual(histRef.current);
    if (ref.current) ref.current.innerHTML = html;
    sincronizarNav();
    foco(); caretAlFinal();
    if (onChange) onChange(html);
  };
  const deshacerAccion = () => { capturarYa(); aplicarHistorial(deshacer(histRef.current)); };
  const rehacerAccion = () => { aplicarHistorial(rehacer(histRef.current)); };

  const onInput = () => {
    emitir(false);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { timerRef.current = null; if (ref.current) capturar(ref.current.innerHTML); }, 350);
  };

  const abrirEnlace = () => {
    const sel = typeof window !== 'undefined' && window.getSelection ? window.getSelection() : null;
    rangoRef.current = sel && sel.rangeCount ? sel.getRangeAt(0).cloneRange() : null;
    setLinkUrl('');
    setLinkAbierto(true);
  };
  const aplicarEnlace = () => {
    const url = linkUrl.trim();
    setLinkAbierto(false);
    if (!url) return;
    capturarYa(); foco();
    const sel = window.getSelection && window.getSelection();
    if (sel && rangoRef.current) { sel.removeAllRanges(); sel.addRange(rangoRef.current); }
    try { document.execCommand('createLink', false, url); } catch (_) { /* jsdom */ }
    capturar(ref.current ? ref.current.innerHTML : ''); emitir(false);
  };

  const onPaste = (e) => {
    e.preventDefault();
    capturarYa();
    const cd = e.clipboardData || (typeof window !== 'undefined' && window.clipboardData);
    const limpio = htmlDesdePegado({ html: cd && cd.getData('text/html'), texto: cd && cd.getData('text/plain') });
    foco();
    let insertado = false;
    try { insertado = document.execCommand('insertHTML', false, limpio); } catch (_) { insertado = false; }
    if (!insertado && ref.current) ref.current.innerHTML = sanearHtmlDescripcion(ref.current.innerHTML + limpio);
    capturar(ref.current ? ref.current.innerHTML : ''); emitir(false);
  };

  return (
    <div className={`ed-wrap${disabled ? ' ed-wrap--disabled' : ''}`}>
      <div className="ed-toolbar" role="toolbar" aria-label="Formato de la descripción">
        <BotonBarra onDo={() => cmd('bold')} title="Negrita" disabled={disabled}><Bold size={15} aria-hidden="true" /></BotonBarra>
        <BotonBarra onDo={() => cmd('italic')} title="Cursiva" disabled={disabled}><Italic size={15} aria-hidden="true" /></BotonBarra>
        <span className="ed-sep" aria-hidden="true" />
        <BotonBarra onDo={() => cmd('insertUnorderedList')} title="Viñetas" disabled={disabled}><List size={15} aria-hidden="true" /></BotonBarra>
        <BotonBarra onDo={() => cmd('insertOrderedList')} title="Lista numerada" disabled={disabled}><ListOrdered size={15} aria-hidden="true" /></BotonBarra>
        <BotonBarra onDo={toggleSubtitulo} title="Subtítulo" disabled={disabled}><Heading size={15} aria-hidden="true" /></BotonBarra>
        <BotonBarra onDo={abrirEnlace} title="Enlace" disabled={disabled}><Link2 size={15} aria-hidden="true" /></BotonBarra>
        <span className="ed-sep" aria-hidden="true" />
        <BotonBarra onDo={deshacerAccion} title="Deshacer" disabled={disabled || !nav.undo}><Undo2 size={15} aria-hidden="true" /></BotonBarra>
        <BotonBarra onDo={rehacerAccion} title="Rehacer" disabled={disabled || !nav.redo}><Redo2 size={15} aria-hidden="true" /></BotonBarra>
      </div>
      {linkAbierto && (
        <div className="ed-link">
          <input type="url" className="ed-link-input" placeholder="https://…" value={linkUrl} autoFocus
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); aplicarEnlace(); } if (e.key === 'Escape') setLinkAbierto(false); }} />
          <button type="button" className="ed-link-ok" onMouseDown={(e) => { e.preventDefault(); aplicarEnlace(); }}>Aplicar</button>
          <button type="button" className="ed-link-x" onMouseDown={(e) => { e.preventDefault(); setLinkAbierto(false); }}>Cancelar</button>
        </div>
      )}
      <div
        id={id}
        ref={ref}
        className="ed-area"
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel}
        onInput={onInput}
        onBlur={() => emitir(true)}
        onPaste={onPaste}
      />
    </div>
  );
}
